import React, { useState, useRef, useEffect, forwardRef } from 'react';
import styles from './SearchableSelect.module.css';

// Helper function to combine CSS classes
const combineClasses = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export interface SearchableSelectProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children' | 'onSelect'> {
  options: string[];
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  hideLabel?: boolean;
  onSelect?: (value: string) => void;
  setUpdating?: (updating: boolean) => void;
  allowManualEntry?: boolean;
}

const SearchableSelect = forwardRef<HTMLInputElement, SearchableSelectProps>(
  ({ options, placeholder, error, helperText, label, required, hideLabel, className, id, onSelect, setUpdating, allowManualEntry = false, value = '', onChange, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [inputValue, setInputValue] = useState(value as string || '');
    const [hasSelection, setHasSelection] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setInputValue(value as string || '');
      setHasSelection(!!(value as string));
    }, [value]);

    useEffect(() => {
      if (!inputValue) {
        setFilteredOptions(options);
      } else if (hasSelection && !isOpen) {
        // Show all options when reopening with an existing selection
        setFilteredOptions(options);
      } else {
        // Always filter when typing or when dropdown is open
        const filtered = options.filter(option =>
          option.toLowerCase().includes(inputValue.toLowerCase())
        );
        setFilteredOptions(filtered);
      }
    }, [inputValue, options, hasSelection, isOpen]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);

          // Reset invalid values when clicking outside (unless manual entry is allowed)
          if (!allowManualEntry && inputValue && !options.includes(inputValue)) {
            const lastValidValue = (value as string) || '';
            setInputValue(lastValidValue);
            setHasSelection(!!lastValidValue);

            // Trigger onChange to sync form with reset value
            if (onChange) {
              const syntheticEvent = {
                target: { value: lastValidValue, name: props.name },
                currentTarget: { value: lastValidValue, name: props.name },
                type: 'change'
              } as React.ChangeEvent<HTMLInputElement>;
              onChange(syntheticEvent);
            }
          } else if (allowManualEntry && inputValue) {
            // Accept manual entry value
            setHasSelection(true);
            if (onChange) {
              const syntheticEvent = {
                target: { value: inputValue, name: props.name },
                currentTarget: { value: inputValue, name: props.name },
                type: 'change'
              } as React.ChangeEvent<HTMLInputElement>;
              onChange(syntheticEvent);
            }
          }

          // Re-enable autosave when clicking outside
          if (setUpdating) {
            setUpdating(false);
          }
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [inputValue, options, value, onChange, setUpdating, props.name, allowManualEntry]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setHasSelection(false);
      setIsOpen(true);

      // Trigger onChange if: value is empty, value is valid option, or manual entry is allowed
      if (onChange && (!newValue || options.includes(newValue) || allowManualEntry)) {
        onChange(e);
      }
    };

    const handleOptionSelect = (option: string) => {
      setInputValue(option);
      setHasSelection(true);
      setIsOpen(false);

      // Re-enable autosave when option is selected
      if (setUpdating) {
        setUpdating(false);
      }

      if (onSelect) {
        onSelect(option);
      }

      // Trigger onChange immediately when option is selected
      if (onChange) {
        const syntheticEvent = {
          target: { value: option, name: props.name },
          currentTarget: { value: option, name: props.name },
          type: 'change'
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    const handleInputFocus = () => {
      if (hasSelection) {
        setInputValue('');
        setHasSelection(false);

        // Trigger onChange when clearing the field
        if (onChange) {
          const syntheticEvent = {
            target: { value: '', name: props.name },
            currentTarget: { value: '', name: props.name },
            type: 'change'
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
      setIsOpen(true);

      // Block autosave while input has focus
      if (setUpdating) {
        setUpdating(true);
      }
    };

    const handleInputBlur = () => {
      // If current input is not a valid option, reset to last valid selection or empty (unless manual entry is allowed)
      if (!allowManualEntry && inputValue && !options.includes(inputValue)) {
        const lastValidValue = (value as string) || '';
        setInputValue(lastValidValue);
        setHasSelection(!!lastValidValue);

        // Trigger onChange to sync form with reset value
        if (onChange) {
          const syntheticEvent = {
            target: { value: lastValidValue, name: props.name },
            currentTarget: { value: lastValidValue, name: props.name },
            type: 'change'
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      } else if (allowManualEntry && inputValue) {
        // Accept manual entry value
        setHasSelection(true);
      }

      // Re-enable autosave when input loses focus
      if (setUpdating) {
        setUpdating(false);
      }
    };

    return (
      <div className={styles.searchableSelectContainer} ref={containerRef}>
        {label && !hideLabel && (
          <label className={styles.label} htmlFor={id}>
            {label}
            {required && ' *'}
          </label>
        )}
        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <div className={styles.searchIcon}>
              <i className="uc-icon icon-size--md" style={{color: 'var(--color-gray-500)'}}>search</i>
            </div>
            <input
              ref={ref}
              id={id}
              type="text"
              value={inputValue}
              onChange={(e) => {
                handleInputChange(e);

                // Call the register's onChange only for valid values or if manual entry is allowed
                if (onChange && (!e.target.value || options.includes(e.target.value) || allowManualEntry)) {
                  onChange(e);
                }
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={placeholder || "Search or select..."}
              autoComplete="off"
              className={combineClasses(
                styles.input,
                error && styles.error,
                className
              )}
              {...props}
            />
          </div>
          {isOpen && filteredOptions.length > 0 && (
            <div className={styles.dropdown}>
              {filteredOptions.map((option) => (
                <div
                  key={option}
                  className={styles.option}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                    handleOptionSelect(option);
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
        {helperText && (
          <span className={error ? styles.errorMessage : styles.helpText}>
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

SearchableSelect.displayName = 'SearchableSelect';

export default SearchableSelect;